import logger from '@/lib/logger';
import { isCurrentEnvironment } from '@/lib/config/environment';
/**
 * Iframe context detection and validation utilities
 *
 * This module provides utilities for detecting when the application is running
 * inside an iframe and validating the parent origin for security.
 */

export interface IframeContext {
  /** Whether the app is currently running inside an iframe */
  isIframe: boolean;
  /** The origin of the parent window (if determinable) */
  parentOrigin: string | null;
  /** List of additional allowed origins from environment config */
  allowedOrigins: string[];
}

/**
 * Get additional allowed origins from environment variable
 * Format: comma-separated list of origins
 */
function getAdditionalOrigins(): string[] {
  if (typeof window === 'undefined') return [];

  const envOrigins = (
    process.env.NEXT_PUBLIC_IFRAME_ALLOWED_ORIGINS || ''
  ).split(',').map(o => o.trim()).filter(Boolean);

  return envOrigins;
}

/**
 * Detect if the application is running inside an iframe
 *
 * Uses window.self !== window.top check, which will also return true
 * if we can't access window.top due to cross-origin restrictions.
 */
export function isInIframe(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    return window.self !== window.top;
  } catch (e) {
    // Cross-origin iframe - can't access window.top property
    // This means we ARE in an iframe
    return true;
  }
}

/**
 * Validate if an origin is in the allowed list
 *
 * @param origin - The origin to validate (e.g., "https://wrapper.neutralaiz.com")
 * @returns true if the origin is allowed
 */
export function isAllowedParentOrigin(origin: string | null): boolean {
  if (!origin) return false;

  // Check against additional origins from environment
  const additionalOrigins = getAdditionalOrigins();
  if (additionalOrigins.includes(origin)) {
    return true;
  }

  // PER-ENV ISOLATION: allow only THIS environment's own domain family (primary +
  // routed alternates + declared trust), host-aware via RuntimeConfig — works for
  // ANY apex (demo.engageiq.ca + engageiq.neutralaiz.com), never a shared
  // *.neutralaiz.com apex of a sibling environment.
  try {
    const url = new URL(origin);
    if (url.protocol !== 'https:') return false;
    return isCurrentEnvironment(url.hostname);
  } catch {
    return false;
  }
}

/**
 * Get the current iframe context
 *
 * @returns IframeContext object with detection results
 */
export function getIframeContext(): IframeContext {
  const isIframe = isInIframe();
  let parentOrigin: string | null = null;

  if (isIframe && typeof window !== 'undefined') {
    // Try to determine parent origin from document.referrer
    // This is the most reliable method when we can't access window.parent.origin
    try {
      const referrer = document.referrer;
      if (referrer) {
        const url = new URL(referrer);
        parentOrigin = url.origin;
      }
    } catch (e) {
      // Can't parse referrer - leave parentOrigin as null
    }
  }

  return {
    isIframe,
    parentOrigin,
    allowedOrigins: getAdditionalOrigins(),
  };
}

/**
 * Check if we're in an iframe context and the parent is authorized
 *
 * SOC2: Returns false if parent origin cannot be determined (fail closed).
 *
 * @returns true if in iframe AND parent origin is allowed
 */
export function isAuthorizedIframeContext(): boolean {
  const context = getIframeContext();
  if (!context.isIframe) return false;

  // If we can determine parent origin, validate it
  if (context.parentOrigin) {
    return isAllowedParentOrigin(context.parentOrigin);
  }

  // SOC2: FAIL CLOSED - if we can't determine parent origin,
  // we cannot authorize the context. This prevents messages
  // from being sent to unknown/malicious parents.
  logger.warn('Context', 'Cannot authorize iframe context: parent origin unknown');
  return false;
}
